import { Component, Host, h, Prop, State, Method, Watch, type JSX, Event, type EventEmitter } from '@stencil/core'
import { type JSXBase } from '@stencil/core/internal'
import { type ISteps } from '../../utils/utils'
import { statuses, type StatusOptions } from '../../utils/statuses'
import { type IMaglrComponent } from '../../utils/IMaglrComponent'
@Component({
    tag: 'maglr-gauge',
    styleUrl: 'maglr-gauge.scss',
    shadow: true,
    assetsDirs: ['editorSettings', 'gauge-assets'],
})

export class MaglrGauge implements IMaglrComponent {
    timer: number = 0
    initValue: number = 0
    currentStep: number = 0
    currentDuration: number = 0
    initialPlay: boolean = true
    @Prop() min: number = 0
    @Prop() max: number = 100
    @Prop() backgroundstroke: number = 20
    @Prop() backgroundstrokecolor: string = '#eee'
    @Prop() percentagestroke: number = 20
    @Prop() percentagestrokecolor: string = '#ff5e00'
    @Prop() mode: string = 'half'
    @Prop() linecap: JSXBase.SVGAttributes<SVGElement>['stroke-linecap'] = 'square'
    @Prop() prefixtext: string = ''
    @Prop() suffixtext: string = '%'
    @Prop() duration: number = 1000
    @Prop() ease: string = 'ease-in-out'
    @Prop() animatenumbers: boolean = false
    @Prop() maglrWidth: number = 10
    @Prop() loopSteps: boolean = true
    @Prop() steps: string = '[{"step":100,"label":"Step 1"}]'
    @Prop() decimals: number = 0
    @Prop() separators: string = 'auto'
    @Prop() leadingzero: boolean = false

    @State() stepState: ISteps[] = []
    @Prop() from: number = 0
    @State() value: number = 0
    @Prop() uuid: string
    @State() status: StatusOptions = statuses.BLANK
    @Prop() autostart: boolean = true

    @Event({
        eventName: 'maglr-webcomponent',
        composed: true,
        cancelable: true,
        bubbles: true,
    })
        fireEvent: EventEmitter

    @Watch('steps')
    parseMyObjectProp (newSteps: string): void {
        this.stepState = JSON.parse(newSteps)
        this.initiateStep(this.currentStep)
    }

    @Method() async start (): Promise<void> {
        if (this.autostart || !this.initialPlay) {
            this.initiateStep(this.currentStep)
            this.status = statuses.STARTED
            this.initialPlay = false
        }
    }

    async next (): Promise<void> {
        if (this.currentStep + 1 < this.stepState.length) {
            this.currentStep++
        } else if (this.loopSteps) {
            this.currentStep = 0
        }
        this.initiateStep(this.currentStep)
    }

    async previous (): Promise<void> {
        if (this.currentStep - 1 >= 0) {
            this.currentStep -= 1
        } else if (this.loopSteps) {
            this.currentStep = this.stepState.length - 1
        }
        this.initiateStep(this.currentStep)
    }

    @Method() async pause (): Promise<void> {
        this.status = statuses.PAUSED
    }

    @Method() async stop (): Promise<void> {
        this.currentDuration = 0
        this.value = this.from
        this.currentStep = 0
        this.status = statuses.STOPPED

        setTimeout(() => {
            this.currentDuration = this.duration
        }, 100)
    }

    @Method() async seek (value = 0): Promise<void> {
        this.from = this.value
        this.value = this.initValue + (this.stepState[this.currentStep].step - this.initValue) * value
    }

    @Method() async restart (): Promise<void> {
        this.currentDuration = 0
        this.value = this.from
        this.currentStep = 0

        setTimeout(() => {
            this.currentDuration = this.duration
            void this.start()
        }, 100)
    }

    @Method() async goToScreenshotState (): Promise<void> {
        this.currentDuration = 0
        this.initiateStep(0)
        void this.start()
    }

    @Method() async goToId (id: string): Promise<void> {
        const step = this.stepState.findIndex(item => item.id === id)
        if (step !== undefined) {
            this.currentStep = step
            this.initiateStep(this.currentStep)
        }
    }

    @Method() async doAction (action: string): Promise<void> {
        switch (action) {
            case 'start':
            case 'play':
                void this.start()
                break
            case 'restart':
                void this.restart()
                break
            case 'stop':
                void this.stop()
                break
            case 'pause':
                void this.pause()
                break
            case 'next':
                void this.next()
                break
            case 'previous':
                void this.previous()
                break
            default:
                void this.goToId(action)
        }
    }

    componentWillLoad (): void {
        this.value = this.from
        this.initValue = this.from
        this.stepState = JSON.parse(this.steps)
        this.status = statuses.LOADED
    }

    componentDidLoad (): void {
        this.currentDuration = this.duration
    }

    initiateStep (step: number): void {
        this.value = this.stepState[step].step
    }

    @Watch('status')
    watchPropHandler (status: number): void {
        this.fireEvent.emit({
            uuid: this.uuid,
            type: 'status',
            status,
        })
    }

    render (): JSX.Element {
        const props = {
            min: this.min,
            max: this.max,
            value: this.value,
            backgroundstroke: this.backgroundstroke,
            backgroundstrokecolor: this.backgroundstrokecolor,
            percentagestroke: this.percentagestroke,
            percentagestrokecolor: this.percentagestrokecolor,
            mode: this.mode,
            linecap: this.linecap,
            prefixtext: this.prefixtext,
            suffixtext: this.suffixtext,
            duration: this.currentDuration,
            ease: this.ease,
            mask: this.currentDuration > 0 ? this.animatenumbers : false,
            maglrWidth: this.maglrWidth,
            style: {
                width: `${this.maglrWidth}px`,
                height: `${this.maglrWidth}px`,
            },
            decimals: this.decimals,
            leadingzero: this.leadingzero,
            maxdigits: Math.max(...this.stepState.map(entry => entry.step.toFixed(0).length)),
        }
        return (
            <Host>
                <div class={'container'}>
                    <gauge-component {...props}></gauge-component>
                    <counter-component {...props} from={this.from} to={this.value} separators={this.separators}></counter-component>
                </div>
            </Host>
        )
    }
}
